import {
  Component,
  ChangeDetectionStrategy,
  OnInit,
  inject,
  signal,
  input,
  output
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ModalComponent } from '@view/shared/molecules/modal/modal.component';
import { ButtonComponent } from '@view/shared/molecules/button/button.component';
import {
  ButtonSizeEnum,
  ButtonTypeEnum
} from '@view/shared/molecules/button/buttonTypesEnum';
import { ObservationListItemDTO } from '@infra-services/observations/api/observations-api.interface';
import {
  ObservationsPresenter,
  StatusOption
} from '@view/preclinical/presenters/observations.presenter';

@Component({
  selector: 'app-observation-edit-modal',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalComponent, ButtonComponent],
  templateUrl: './observation-edit-modal.component.html',
  styleUrls: ['./observation-edit-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ObservationEditModalComponent implements OnInit {
  readonly observation = input.required<ObservationListItemDTO>();
  readonly closeModal = output();
  readonly statusUpdated = output();

  readonly ButtonTypeEnum = ButtonTypeEnum;
  readonly ButtonSizeEnum = ButtonSizeEnum;

  private readonly presenter = inject(ObservationsPresenter);

  private readonly _isLoading = signal(false);
  readonly isLoading = this._isLoading.asReadonly();

  private readonly _isLoadingStatuses = signal(true);
  readonly isLoadingStatuses = this._isLoadingStatuses.asReadonly();

  selectedStatusId = '';
  private initialStatusId = '';
  statusOptions = signal<StatusOption[]>([]);

  ngOnInit(): void {
    this.loadReprocessStatuses();
  }

  private loadReprocessStatuses(): void {
    this._isLoadingStatuses.set(true);
    this.presenter.loadReprocessStatuses().subscribe({
      next: (statuses) => {
        this.statusOptions.set(statuses);
        this.selectedStatusId = this.getCurrentStatusId(statuses);
        this.initialStatusId = this.selectedStatusId;
        this._isLoadingStatuses.set(false);
      },
      error: (error) => {
        console.error('Error loading reprocess statuses:', error);
        this._isLoadingStatuses.set(false);
      }
    });
  }

  private getCurrentStatusId(statuses: StatusOption[]): string {
    const currentStatus = this.observation().reprocessStatus?.toUpperCase();
    if (!currentStatus) {
      return statuses[0]?.id || '';
    }

    const matchedStatus = statuses.find(
      (s) =>
        s.name.toUpperCase() === currentStatus ||
        s.name.toUpperCase().includes(currentStatus) ||
        currentStatus.includes(s.name.toUpperCase())
    );

    return matchedStatus?.id || statuses[0]?.id || '';
  }

  isResolved(): boolean {
    const currentStatus = this.observation().reprocessStatus?.toUpperCase();
    return currentStatus?.includes('SOLUCIONADO') || false;
  }

  hasStatusChanged(): boolean {
    return this.selectedStatusId !== this.initialStatusId;
  }

  canSave(): boolean {
    return this.hasStatusChanged() && !this.isLoading() && !this.isResolved();
  }

  onClose(): void {
    this.closeModal.emit();
  }

  onSave(): void {
    if (!this.canSave()) {
      return;
    }

    this._isLoading.set(true);

    this.presenter
      .updateObservationStatus(
        this.observation().observationId,
        this.selectedStatusId
      )
      .subscribe({
        next: () => {
          this._isLoading.set(false);
          this.statusUpdated.emit();
        },
        error: (error) => {
          console.error('Error updating status:', error);
          this._isLoading.set(false);
        }
      });
  }
}
